import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { languages } from '../data/mock';
import { BookOpen, Mail, Lock, User, Globe, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isLoading, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    targetLanguage: 'de'
  });
  const [errors, setErrors] = useState({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/stories';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Get mode from URL or query params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const modeParam = urlParams.get('mode');
    if (modeParam === 'register') {
      setMode('register');
    }
  }, [location]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (mode === 'register' && formData.password.length < 6) {
      newErrors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
    }

    if (mode === 'register' && !formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      let result;
      if (mode === 'login') {
        result = await login(formData.email, formData.password);
      } else {
        result = await register(formData);
      }

      if (result.success) {
        toast({
          title: mode === 'login' ? 'Anmeldung erfolgreich' : 'Registrierung erfolgreich',
          description: `Willkommen${mode === 'register' ? ' bei DMV-Lernportal' : ' zurück'}!`,
        });
        
        const from = location.state?.from?.pathname || '/stories';
        navigate(from, { replace: true });
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Ein unerwarteter Fehler ist aufgetreten' });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setErrors({});
    setFormData({
      email: formData.email,
      password: '',
      name: '',
      targetLanguage: formData.targetLanguage
    });
  };

  const fillDemoData = () => {
    setFormData({
      ...formData,
      email: 'demo@dmv-lernportal.de',
      password: 'demo123'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="absolute top-4 left-4 p-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center justify-center space-x-2 mb-4">
            <BookOpen className="h-8 w-8 text-[#4ECDC4]" />
            <span className="text-2xl font-bold text-slate-900">DMV-Lernportal</span>
          </div>
          <p className="text-slate-600">
            {mode === 'login' ? 'Melden Sie sich an, um fortzufahren' : 'Erstellen Sie Ihr Konto'}
          </p>
        </div>

        {/* Auth Card */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-center">
              {mode === 'login' ? 'Anmelden' : 'Registrieren'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Demo Login Helper */}
            {mode === 'login' && (
              <Alert className="bg-[#4ECDC4] bg-opacity-10 border-[#4ECDC4]">
                <AlertDescription className="text-center">
                  <strong>Demo-Zugang:</strong>{' '}
                  <Button 
                    variant="link" 
                    className="text-[#4ECDC4] p-0 h-auto"
                    onClick={fillDemoData}
                  >
                    Demo-Daten verwenden
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Error Message */}
            {errors.general && (
              <Alert variant="destructive">
                <AlertDescription>{errors.general}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name (Register only) */}
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Ihr vollständiger Name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`pl-10 ${errors.name ? 'border-red-300' : ''}`}
                    />
                  </div>
                  {errors.name && (
                    <p className="text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ihre@email.de"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`pl-10 ${errors.email ? 'border-red-300' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={mode === 'register' ? 'Mindestens 6 Zeichen' : 'Ihr Passwort'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`pl-10 pr-10 ${errors.password ? 'border-red-300' : ''}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 p-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-slate-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-400" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* Target Language (Register only) */}
              {mode === 'register' && (
                <div className="space-y-2">
                  <Label htmlFor="targetLanguage">Lernsprache</Label>
                  <Select 
                    value={formData.targetLanguage} 
                    onValueChange={(value) => handleInputChange('targetLanguage', value)}
                  >
                    <SelectTrigger className="w-full">
                      <Globe className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <div className="flex items-center space-x-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa] text-white h-11"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  mode === 'login' ? 'Anmelden' : 'Konto erstellen'
                )}
              </Button>
            </form>

            <Separator />

            {/* Switch Mode */}
            <div className="text-center">
              <p className="text-sm text-slate-600">
                {mode === 'login' ? 'Noch kein Konto?' : 'Bereits ein Konto?'}
                {' '}
                <Button
                  variant="link"
                  className="text-[#4ECDC4] p-0 h-auto"
                  onClick={switchMode}
                >
                  {mode === 'login' ? 'Jetzt registrieren' : 'Hier anmelden'}
                </Button>
              </p>
            </div>

            {/* Features Preview */}
            {mode === 'register' && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium text-slate-900 text-sm">Mit Ihrem Konto erhalten Sie:</h4>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Persönlichen Lernfortschritt speichern</li>
                  <li>• Individuelle Vokabellisten erstellen</li>
                  <li>• Premium Text-to-Speech Stimmen</li>
                  <li>• Detaillierte Lernstatistiken</li>
                  <li>• Synchronisation zwischen Geräten</li>
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-slate-500">
          <p>
            Mit der Nutzung stimmen Sie unseren{' '}
            <Button variant="link" className="text-xs p-0 h-auto">
              Nutzungsbedingungen
            </Button>{' '}
            und{' '}
            <Button variant="link" className="text-xs p-0 h-auto">
              Datenschutzrichtlinien
            </Button>{' '}
            zu.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;